import flatten from 'lodash/flatten';
import zip from 'lodash/zip';
import differenceBy from 'lodash/differenceBy';

import {ColorChoice, DateKeyedFunnelSegments, FunnelSegment} from 'common/report/funnels/models/types';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {getTopPathRenamedSegments} from 'common/report/funnels/models/utils';
import {createBlankSteps} from 'common/report/funnels/util';

export function getSegmentSteps(
  query: FunnelsQuery,
  selected: ColorChoice[],
  segmentData: DateKeyedFunnelSegments[],
  comparisonSegmentData: DateKeyedFunnelSegments[],
  fromComparisonData = false,
): FunnelSegment[] {
  const selectedMap = new Map<string, number>(selected.map((s, index) => [s.key, index] as [string, number]));

  return (
    getTopPathRenamedSegments(query, fromComparisonData ? comparisonSegmentData : segmentData)
      .filter((segment) => selectedMap.has(segment.prop))
      // sort by index (keep them in the same order as the table)
      .sort((a, b) => selectedMap.get(a.prop) - selectedMap.get(b.prop))
  );
}

export function getSegmentStepsWithComparison(
  query: FunnelsQuery,
  selected: ColorChoice[],
  segmentData: DateKeyedFunnelSegments[],
  comparisonSegmentData: DateKeyedFunnelSegments[],
): FunnelSegment[] {
  const segments = getSegmentSteps(query, selected, segmentData, comparisonSegmentData);
  const comparisonSegments = reconcileComparisonSegments({
    segments,
    comparisonSegments: getSegmentSteps(query, selected, segmentData, comparisonSegmentData, true),
  });
  return flatten(zip(comparisonSegments, segments));
}

/**
 * Removes segments from 'comparisonSegments' that don't exist in 'segments', sorts in the same order as 'segments',
 * and adds missing (blank) segments to 'comparisonSegments'.
 */
export function reconcileComparisonSegments({
  segments,
  comparisonSegments,
  updatePropName = false,
}: {
  segments: FunnelSegment[];
  comparisonSegments: FunnelSegment[];
  updatePropName?: boolean;
}): FunnelSegment[] {
  const propToIndexMap = new Map(segments.map((segment, index) => [segment.prop, index]));

  // Remove segments that don't exist in 'segments'
  comparisonSegments = comparisonSegments.filter((segment) => propToIndexMap.get(segment.prop) !== undefined);

  // Match order of 'segments'
  comparisonSegments.sort((a, b) => propToIndexMap.get(a.prop) - propToIndexMap.get(b.prop));

  // Add blank segments
  const missingSegments = differenceBy(segments, comparisonSegments, `prop`);
  const blankMissingSegmentsWithIndex: Array<[FunnelSegment, number]> = missingSegments.map((segment) => [
    {...segment, ...{steps: createBlankSteps(segment.steps)}},
    segments.indexOf(segment),
  ]);
  blankMissingSegmentsWithIndex.forEach(([segment, index]) => comparisonSegments.splice(index, 0, segment));

  if (updatePropName) {
    comparisonSegments = comparisonSegments.map((segment) => ({...segment, propName: `${segment.propName} (past)`}));
  }

  return comparisonSegments;
}
